import { useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import KendoInput from "assets/theme/components/inputKendo";
import CopyableError from "afms/components/CopyableError";
import { setDevicePin } from "api/signIn.api";
import { readQuickSignIn, saveQuickSignIn } from "utils/quickSignIn";

const PIN_RE = /^\d{4,6}$/;

// Sets (or replaces) the quick sign-in PIN for this device. The server returns a
// device key; this browser keeps it, and the PIN works only together with it.
function SetPinDialog({ open, onClose, onSaved }) {
  const user = useSelector((state) => state.auth.user);
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const close = () => {
    setPin("");
    setConfirm("");
    setError("");
    onClose();
  };

  const save = async () => {
    if (!PIN_RE.test(pin)) {
      setError("A PIN is 4 to 6 digits.");
      return;
    }
    if (pin !== confirm) {
      setError("The two PINs are different.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const earlier = readQuickSignIn();
      const res = await setDevicePin({ pin, deviceKey: earlier?.deviceKey });
      const { id, deviceKey } = res?.data?.data || {};
      const profile = user?.userProfile || {};
      saveQuickSignIn({
        id,
        deviceKey,
        name: profile.firstName || profile.email || "",
        signInName: profile.email || profile.username || "",
      });
      setPin("");
      setConfirm("");
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Unable to set the PIN.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="xs">
      <DialogTitle>
        <SoftTypography variant="h6" fontWeight="bold">
          Quick sign-in PIN
        </SoftTypography>
        <SoftTypography variant="caption" color="text">
          Next time on this device, sign in with this PIN instead of your password. Five wrong
          tries lock it until you sign in with your password again.
        </SoftTypography>
      </DialogTitle>
      <DialogContent dividers>
        <SoftBox mb={2}>
          <SoftTypography component="label" variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.75 }}>
            PIN (4 to 6 digits)
          </SoftTypography>
          <KendoInput
            type="password"
            name="pin"
            inputMode="numeric"
            maxLength={6}
            autoComplete="off"
            ariaLabel="PIN"
            value={pin}
            onChange={(e) => setPin(String(e.target.value).replace(/\D/g, ""))}
          />
        </SoftBox>
        <SoftBox mb={1}>
          <SoftTypography component="label" variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.75 }}>
            Confirm PIN
          </SoftTypography>
          <KendoInput
            type="password"
            name="confirmPin"
            inputMode="numeric"
            maxLength={6}
            autoComplete="off"
            ariaLabel="Confirm PIN"
            value={confirm}
            onChange={(e) => setConfirm(String(e.target.value).replace(/\D/g, ""))}
          />
        </SoftBox>
        {error && <CopyableError message={error} />}
      </DialogContent>
      <DialogActions>
        <SoftButton variant="outlined" color="dark" size="small" onClick={close}>
          Cancel
        </SoftButton>
        <SoftButton variant="gradient" color="info" size="small" onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Set PIN"}
        </SoftButton>
      </DialogActions>
    </Dialog>
  );
}

SetPinDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
};

SetPinDialog.defaultProps = {
  onSaved: undefined,
};

export default SetPinDialog;
